import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Search, Edit, Eye, Calendar, Plus, Archive } from "lucide-react"
import { useSeasons } from "@/graphql/hooks/useSeasons"
import { Skeleton } from "@/components/ui/skeleton"
import { useNavigate } from "react-router-dom"
import { SeasonEditModal } from "./SeasonEditModal"
import type { Season } from "@/graphql/types"

export function SeasonsManagement() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [editingSeason, setEditingSeason] = useState<Season | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const { seasons, loading, error, refetch } = useSeasons()

  const filteredSeasons =
    seasons
      ?.filter((season) => season.Game.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .filter((season) => season.Game.isActive === 1)
      .sort((a, b) => b.isActive - a.isActive)
      .sort((a, b) => a.isArchived - b.isArchived)
      .sort((a, b) => a.gameId - b.gameId) || []

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error cargando temporadas: {error.message}
            <Button onClick={() => refetch()} className="ml-4" variant="outline">
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("es-CL", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar temporadas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          onClick={() => {
            setIsNew(true)
            setIsEditModalOpen(true)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Temporada
        </Button>
      </div>

      <Card className="bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Temporadas de Juego ({filteredSeasons.length})
          </CardTitle>
        </CardHeader>

        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100 dark:bg-gray-800">
                <TableHead className="text-gray-700 dark:text-gray-300">Nombre</TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300">Juego</TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300">Fecha Inicio</TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300">Fecha Fin</TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300">Estado</TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300">Visibilidad</TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300">Torneos</TableHead>
                <TableHead className="text-right text-gray-700 dark:text-gray-300">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filteredSeasons.map((season) => (
                <TableRow key={season.id} className="border-t border-gray-200 dark:border-gray-700">
                  <TableCell className="font-medium">{season.name}</TableCell>

                  <TableCell className="flex items-center gap-2">
                    {season.Game?.urlImage && (
                      <img
                        src={season.Game.urlImage}
                        alt={season.Game.name}
                        className="w-6 h-6 rounded object-cover"
                      />
                    )}
                    {season.Game?.name || "N/A"}
                  </TableCell>

                  <TableCell>{formatDate(season.startDate)}</TableCell>
                  <TableCell>{formatDate(season.endDate)}</TableCell>

                  <TableCell>
                    <Badge
                      variant={season.isActive ? "secondary" : "destructive"}
                      className="dark:border-gray-600"
                    >
                      {season.isActive ? "Activa" : "Inactiva"}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={season.isArchived ? "outline" : "outline"}
                      className={`flex items-center gap-1 px-2 py-1 rounded-md ${
                        season.isArchived
                          ? "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                          : "bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100"
                      }`}
                    >
                      {season.isArchived ? (
                        <>
                          <Archive className="h-4 w-4" />
                          Archivada
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4" />
                          Visible
                        </>
                      )}
                    </Badge>
                  </TableCell>

                  <TableCell>{season.totalTournaments || 0}</TableCell>

                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-300 dark:border-gray-600"
                        onClick={() => navigate(season.rankingUrl)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-300 dark:border-gray-600"
                        onClick={() => {
                          setEditingSeason(season)
                          setIsNew(false)
                          setIsEditModalOpen(true)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SeasonEditModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        season={editingSeason}
        onSuccess={refetch}
        isNew={isNew}
      />
    </div>
  )
}
